import React, { Component } from 'react';

import appSettings from '../../config/settings';
import * as log from '../../util/log';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

const RELOAD_COOLDOWN_MS = 60000; // 1 minute

const getIsChunkError = error => {
  const message = error?.message || '';
  const name = error?.name || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading (?:chunk|CSS chunk) .+ failed/i.test(message)
  );
};

// Use ErrorBoyndary to catch ChunkLoadError
// https://reactjs.org/docs/error-boundaries.html
class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const isChunkError = getIsChunkError(error);

    if (isChunkError && typeof window !== 'undefined') {
      // Chunk missing after a new deploy — reload once to pick up the fresh bundle.
      // We use a timestamp to allow retrying if the error persists or happens again later (e.g. another deploy).
      const now = Date.now();
      const lastReloadAt = parseInt(sessionStorage.getItem('chunk_reload_attempted_at') || '0', 10);

      if (now - lastReloadAt > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem('chunk_reload_attempted_at', now.toString());
        window.location.reload();
        return;
      }

      // If we are here, it means the reload didn't fix the chunk error within the cooldown period.
      // We log it as a warning instead of a full error to avoid noise, as it's often a caching/infra issue.
      log.warn(error, 'chunk-load-error-persistent', {
        boundary: 'LoadableComponentErrorBoundary',
        componentStack: errorInfo?.componentStack,
      });
      return;
    }

    log.error(error, 'react-error-boundary-failed', {
      boundary: 'LoadableComponentErrorBoundary',
      componentStack: errorInfo?.componentStack,
      errorName: error?.name,
    });
  }

  render() {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }

    const isChunkError = getIsChunkError(error);
    if (isChunkError) {
      const now = Date.now();
      const lastReloadAt =
        typeof window !== 'undefined'
          ? parseInt(sessionStorage.getItem('chunk_reload_attempted_at') || '0', 10)
          : 0;

      if (now - lastReloadAt > RELOAD_COOLDOWN_MS) {
        // Hide the error UI while we wait for the reload triggered in componentDidCatch
        return null;
      }
    }

    return <LoadableComponentErrorBoundaryPage />;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
